/* pRo8vjsS / CLYIqlwM — Wishlist client (Path B, Firebase-native).
 *
 * Split from dTq7HgGX: the App Proxy path (/apps/wishlist) is dead-ended on the
 * expired custom-install link (Shopify 404s / re-consent wall). Customers already
 * sign in to the storefront via studio-prod Firebase (firebase-auth.js, theme #68),
 * so we call the studio-backend /wishlist route directly with the Firebase ID token.
 * requireAuth verifies the token (+ email_verified) and keys the wishlist on the UID
 * in Firestore — no Shopify app, proxy, scope, or re-consent needed.
 *
 * Config comes from window.__wishlist (emitted in layout/theme.liquid):
 *   { backendUrl: string, loginUrl: string }
 * Auth state is NOT read from Liquid `customer` (Firebase sign-in never mints a
 * Shopify session, so that flag is always false here) — it is observed live from
 * the same Firebase app instance the header menu / My Studio use.
 *
 * Contract (studio-backend, mirrors the old proxy shape so the swap is mechanical):
 *   GET    /wishlist            -> { models: [slug], loggedIn: true }
 *   POST   /wishlist  {model}   -> { models, added }
 *   DELETE /wishlist/:slug      -> { models, removed }
 *   Guest / unverified          -> 401/403  (treated as not-signed-in)
 */
import { getApp, getApps } from 'https://www.gstatic.com/firebasejs/10.14.1/firebase-app.js';
import { getAuth, onAuthStateChanged } from 'https://www.gstatic.com/firebasejs/10.14.1/firebase-auth.js';
import { authedFetch, syncVerifyPrompt } from '@studio/verify-email';

(function () {
  'use strict';

  var cfg = window.__wishlist || { backendUrl: '', loginUrl: '/pages/signin' };
  var BACKEND = (cfg.backendUrl || '').replace(/\/$/, '');
  // loggedIn is resolved live from Firebase (see onAuthStateChanged below); it is
  // NOT taken from Liquid because Firebase auth is not a Shopify customer session.
  var loggedIn = false;

  // Guard: firebase-auth.js must have called initializeApp() first. If not, the
  // hearts fall back to routing guests to sign-in (no console errors).
  var auth = getApps().length ? getAuth(getApp()) : null;

  var t = (window.Shopify && window.Shopify.wishlistStrings) || {};
  function str(key, fallback, name) {
    var s = t[key] || fallback;
    return name ? s.replace('%{name}', name) : s;
  }

  // ---- Toast -------------------------------------------------------------
  var toastEl;
  function toast(message, actionLabel, actionHref) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'wishlist-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.innerHTML = '';
    var span = document.createElement('span');
    span.textContent = message;
    toastEl.appendChild(span);
    if (actionLabel && actionHref) {
      var a = document.createElement('a');
      a.href = actionHref;
      a.textContent = actionLabel;
      a.className = 'wishlist-toast__action';
      toastEl.appendChild(a);
    }
    toastEl.classList.add('is-visible');
    clearTimeout(toastEl._timer);
    toastEl._timer = setTimeout(function () { toastEl.classList.remove('is-visible'); }, 4000);
  }

  // ---- Login prompt modal (guest) ---------------------------------------
  // CTA routes to the Firebase sign-in page (/pages/signin), carrying a
  // return_url + #wishlist-pending replay so the save completes after login.
  function promptLogin(slug) {
    var pendingHash = slug ? '#wishlist-pending=' + encodeURIComponent(slug) : '';
    var returnUrl = window.location.pathname + window.location.search + pendingHash;
    // EAPAHvZu — compose through the ONE helper (window.__studioAuth.withReturn,
    // emitted in layout/theme.liquid from snippets/studio-signin-url). cfg.loginUrl
    // is the same rendered value; pass it as the base so this call site keeps its
    // own config without re-spelling the join.
    var base = cfg.loginUrl || '/pages/signin';
    var loginUrl = (window.__studioAuth && window.__studioAuth.withReturn)
      ? window.__studioAuth.withReturn(returnUrl, base)
      : base + '?return_url=' + encodeURIComponent(returnUrl);

    var overlay = document.createElement('div');
    overlay.className = 'wishlist-modal';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', str('login_title', 'Log in to save'));

    // DOM-built (never innerHTML) so cfg.loginUrl / location-derived strings
    // can never be parsed as markup (XSS).
    var panel = document.createElement('div');
    panel.className = 'wishlist-modal__panel';

    var title = document.createElement('p');
    title.className = 'wishlist-modal__title';
    title.textContent = str('login_title', 'Log in to save');
    panel.appendChild(title);

    var body = document.createElement('p');
    body.className = 'wishlist-modal__body';
    body.textContent = str('login_body', 'Sign in to keep your favourite models in one place — on any device.');
    panel.appendChild(body);

    var actions = document.createElement('div');
    actions.className = 'wishlist-modal__actions';

    var cta = document.createElement('a');
    cta.className = 'wishlist-modal__cta';
    cta.href = loginUrl;
    cta.textContent = str('login_cta', 'Log in');
    actions.appendChild(cta);

    var dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.className = 'wishlist-modal__dismiss';
    dismiss.setAttribute('data-wishlist-dismiss', '');
    dismiss.textContent = str('login_cancel', 'Not now');
    actions.appendChild(dismiss);

    panel.appendChild(actions);
    overlay.appendChild(panel);

    function close() { overlay.remove(); document.removeEventListener('keydown', onKey); }
    function onKey(e) { if (e.key === 'Escape') close(); }
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay || e.target.hasAttribute('data-wishlist-dismiss')) close();
    });
    document.addEventListener('keydown', onKey);
    document.body.appendChild(overlay);
    cta.focus();
  }

  // uGqPRiwp — the 403 toast is the one that must stay on screen: it carries the
  // "Resend verification email" control, and a 4-second auto-dismiss would take
  // the only way out of the dead end away with it.
  function verifyToast() {
    toast(str('verify_toast', 'Verify your email to save models to your wishlist.'));
    clearTimeout(toastEl._timer);
    syncVerifyPrompt(toastEl, 403);
  }

  // ---- API ---------------------------------------------------------------
  // Every call carries the Firebase ID token. 401 means the session isn't usable
  // (signed out or expired) → code 'login'.
  //
  // uGqPRiwp: 403 used to be folded into the same 'login' code, which opened the
  // "Log in to save" modal at a customer who was ALREADY logged in — a dead end
  // dressed as the wrong instruction. 403 is the backend's email_verified gate
  // and gets its own code, so the toast can offer the resend control.
  //
  // The token, the Authorization header and the retry-once on that gate live in
  // ONE place — authedFetch (studio-verify-email.js); this wrapper only maps the
  // outcome onto the wishlist's own error codes.
  function loginError() {
    loggedIn = false; var e = new Error('login'); e.code = 'login'; return e;
  }
  function wishlistFetch(path, opts) {
    return authedFetch(BACKEND + path, opts).then(function (res) {
      if (res.status === 401) throw loginError();
      if (res.status === 403) {
        var v = new Error('verify'); v.code = 'verify'; v.status = 403; throw v;
      }
      if (!res.ok) throw new Error('http ' + res.status);
      return res.json();
    }, function (err) {
      if (err && err.message === 'not-signed-in') throw loginError();
      throw err;
    });
  }

  function save(slug, name) {
    return wishlistFetch('/wishlist', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ model: slug, name: name }),
    });
  }
  function remove(slug) {
    return wishlistFetch('/wishlist/' + encodeURIComponent(slug), { method: 'DELETE' });
  }

  // ---- Server-render reconcile (first-paint fill) ------------------------
  // Liquid can't render Firebase-scoped state, so hearts paint as "outline".
  // Once auth resolves, GET the list and flip the saved hearts to filled.
  function syncHearts() {
    if (!loggedIn) return;
    wishlistFetch('/wishlist', { method: 'GET' }).then(function (data) {
      var saved = {};
      (data.models || []).forEach(function (slug) { saved[slug] = true; });
      document.querySelectorAll('[data-wishlist-heart]').forEach(function (btn) {
        var slug = btn.getAttribute('data-model-slug');
        btn.setAttribute('aria-pressed', saved[slug] ? 'true' : 'false');
      });
      document.dispatchEvent(new CustomEvent('wishlist:change', {
        detail: { models: data.models, sync: true },
      }));
    }).catch(function (err) {
      // Non-fatal for hearts (they stay outline until interacted), but a surface
      // that RENDERS this list — /pages/wishlist — must be able to tell "load
      // failed" from "empty wishlist". EAPAHvZu: an empty state shown to someone
      // with a full wishlist is the same class of lie as a gate that never clears.
      document.dispatchEvent(new CustomEvent('wishlist:error', {
        detail: { code: (err && err.code) || 'error' },
      }));
    });
  }

  // ---- Heart toggle ------------------------------------------------------
  function onHeartClick(btn) {
    var slug = btn.getAttribute('data-model-slug');
    var name = btn.getAttribute('data-model-name') || slug;
    if (!slug) return;

    if (!loggedIn) { promptLogin(slug); return; }

    var wasSaved = btn.getAttribute('aria-pressed') === 'true';
    var action = wasSaved ? 'remove' : 'save';
    // Optimistic flip.
    btn.setAttribute('aria-pressed', String(!wasSaved));
    btn.disabled = true;

    var req = wasSaved ? remove(slug) : save(slug, name);
    req.then(function (data) {
      btn.disabled = false;
      document.dispatchEvent(new CustomEvent('wishlist:change', {
        detail: { slug: slug, name: name, saved: action === 'save', models: data.models },
      }));
      if (action === 'save') {
        toast(str('saved_toast', 'Saved %{name} to your wishlist', name),
          str('view', 'View wishlist'), '/pages/wishlist');
      } else {
        toast(str('removed_toast', 'Removed %{name}', name));
      }
    }).catch(function (err) {
      btn.disabled = false;
      btn.setAttribute('aria-pressed', String(wasSaved)); // revert
      if (err.code === 'login') { promptLogin(slug); return; }
      if (err.code === 'verify') { verifyToast(); return; }
      toast(str('error_toast', "Couldn't update your wishlist — try again"));
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('[data-wishlist-heart]') : null;
    if (btn) { e.preventDefault(); onHeartClick(btn); }
  });

  // ---- Post-login pending-save replay -----------------------------------
  // After a guest signs in from the "Log in to save" modal, they return with
  // #wishlist-pending=<slug>. Replay the save once, then scrub the hash.
  function replayPending() {
    var m = /(?:^|#|&)wishlist-pending=([^&]+)/.exec(window.location.hash);
    if (!m || !loggedIn) return;
    var slug = decodeURIComponent(m[1]);
    // Clean the hash immediately so a refresh doesn't re-fire.
    history.replaceState(null, '', window.location.pathname + window.location.search);
    var btn = document.querySelector('[data-wishlist-heart][data-model-slug="' + (window.CSS && CSS.escape ? CSS.escape(slug) : slug) + '"]');
    var name = btn ? btn.getAttribute('data-model-name') : slug;
    var wasPressed = btn ? btn.getAttribute('aria-pressed') : null;
    if (btn) btn.setAttribute('aria-pressed', 'true');
    save(slug, name).then(function (data) {
      document.dispatchEvent(new CustomEvent('wishlist:change', {
        detail: { slug: slug, name: name, saved: true, models: data.models },
      }));
      toast(str('saved_toast', 'Saved %{name} to your wishlist', name),
        str('view', 'View wishlist'), '/pages/wishlist');
    }).catch(function (err) {
      // Roll back the optimistic fill — the save didn't land.
      if (btn) btn.setAttribute('aria-pressed', wasPressed || 'false');
      // ⚠️ THIS IS THE NEW EMAIL/PASSWORD CUSTOMER'S OWN ROUTE: guest clicks the
      // heart → "Log in to save" → registers → returns with #wishlist-pending=
      // → replay → 403. The heart's own catch has carried the `verify` branch
      // since this card's first fix; the replay's did not, so the one path that
      // only an unverified new customer takes was the one path left silent.
      if (err && err.code === 'verify') { verifyToast(); return; }
      if (err && err.code === 'login') { promptLogin(slug); return; }
      toast(str('error_toast', "Couldn't update your wishlist — try again"));
    });
  }

  // ---- Boot: observe Firebase auth, then reconcile + replay --------------
  // onAuthStateChanged fires once auth resolves (signed-in or not). This is the
  // single source of `loggedIn` — replacing the dead Shopify `customer` flag.
  if (auth) {
    onAuthStateChanged(auth, function (user) {
      loggedIn = !!user;
      if (loggedIn) {
        syncHearts();
        replayPending();
      }
    });
  }
})();
